import type { FieldDefinitionType } from '#root/components/dynamicForm/types/field.type.ts';
import { useLanguage } from '#root/composables/useLanguage.ts';
import type {
  CardCheckBoxesItem,
  CardCheckBoxesPropsType,
  InputSearchModelType
} from '@c4-links/vue3-links-components';
import { type FilterDefinitionValue } from '@carrefour-gcs/bff-sdk';
import { computed, ref, watch, type Ref } from 'vue';

export type CheckboxCardSelectionType = {
  [groupId: string]: CardCheckBoxesItem<string>[];
};

/**
 * composable for orchestrating checkbox group filter logic
 */
export const useCheckboxGroupSelection = ({
  field,
  searchData,
  searchValue,
  initialValue,
  onSelectionChange
}: {
  initialValue: Ref<unknown>;
  field: FieldDefinitionType;
  searchValue: Ref<InputSearchModelType | undefined>;
  searchData: Readonly<Ref<FilterDefinitionValue[] | undefined>>;
  onSelectionChange: (selections: string[]) => void;
}) => {
  const { t } = useLanguage();

  const modelValue = ref<CheckboxCardSelectionType>({});
  const checkBoxOptions = ref<FilterDefinitionValue[]>(field.options ?? []);

  const nbSelectedCheckboxGroupItems = computed(
    () =>
      Object.values(modelValue.value)
        .filter((group) => group.length > 0)
        .flatMap((v) => v.map((item) => item.checked === true)).length
  );

  const checkboxGroupValues = computed<CardCheckBoxesPropsType<string>[]>(() => {
    const res: CardCheckBoxesPropsType<string>[] = [];

    for (const option of checkBoxOptions.value) {
      if (!option.children || option.children.length === 0) {
        continue;
      }

      res.push({
        activeShadow: true,
        id: `checkbox-group-${option.label}`,
        title: t(`filters.form.value.title.${option.label}`),
        description: t(`filters.form.value.description.${option.value}`),
        checkboxOptions: option.children.map((child) => ({
          value: child.value,
          id: `checkbox-group-${child.label}`,
          label: t(`filters.form.value.${child.label}`),
          checked:
            modelValue.value[`checkbox-group-${option.label}`]?.some((item) => item.value === child.value) ?? false
        }))
      });
    }

    return res;
  });

  // ———— B ———— Functions ————————————————————————————————————————————————————————————————————————————————

  const initialize = () => {
    const groupSelection: CheckboxCardSelectionType = {};

    if (initialValue.value) {
      const raw = initialValue.value as string[];

      checkboxGroupValues.value.forEach((group) => {
        groupSelection[group.id as string] = [];

        group.checkboxOptions.forEach((option) => {
          if (raw.includes(option.value)) {
            option.checked = true;

            groupSelection[group.id as string]?.push({
              checked: true,
              value: option.value,
              indeterminate: false
            });
          }
        });
      });

      modelValue.value = groupSelection;
    }
  };

  const selectAll = () => {
    const groupSelection: CheckboxCardSelectionType = {};

    checkboxGroupValues.value.forEach((group) => {
      groupSelection[group.id as string] = [];

      group.checkboxOptions.forEach((option) => {
        option.checked = true;

        groupSelection[group.id as string]?.push({
          checked: true,
          value: option.value,
          indeterminate: false
        });
      });
    });

    modelValue.value = groupSelection;

    onSelectionChange(
      Object.values(groupSelection)
        .flat()
        .map((item) => item.value)
    );
  };

  const deselectAll = () => {
    checkboxGroupValues.value.forEach((group) => {
      group.checkboxOptions.forEach((option) => {
        option.checked = false;
      });

      modelValue.value[group.id as string] = [];
    });

    onSelectionChange([]);
  };

  // ———— E ———— Functions ————————————————————————————————————————————————————————————————————————————————

  // ———— B ———— Watchers ————————————————————————————————————————————————————————————————————————————————

  /**
   * Rebuilds the checkbox options when search results are available.
   */
  watch(
    [searchData, searchValue],
    ([newData, newSearchValue]) => {
      if (
        newData &&
        newData.length > 0 &&
        (newSearchValue?.searchValue !== '' || newSearchValue?.searchValue.length > 0)
      ) {
        checkBoxOptions.value = newData;
      } else if (
        ((newData && newData.length === 0) || !newData) &&
        (newSearchValue?.searchValue === '' || !newSearchValue?.searchValue || newSearchValue?.searchValue.length === 0)
      ) {
        checkBoxOptions.value = field.options ?? [];
      } else if (newData && newData.length === 0 && newSearchValue?.searchValue) {
        checkBoxOptions.value = [];
      }
    },
    { deep: true }
  );

  watch(
    modelValue,
    (newValue) => {
      const selectedValues: string[] = [];

      Object.values(newValue).forEach((groupSelection) => {
        if (Array.isArray(groupSelection)) {
          groupSelection.forEach((item) => {
            if (item.checked) {
              selectedValues.push(item.value as string);
            }
          });
        }
      });

      onSelectionChange(selectedValues);
    },
    { deep: true }
  );

  // ———— E ———— Watchers ————————————————————————————————————————————————————————————————————————————————

  return {
    modelValue,
    checkboxGroupValues,
    nbSelectedCheckboxGroupItems,

    selectAll,
    initialize,
    deselectAll
  };
};
